// Audio cache manager for efficient audio file caching

import { ChannelDataSource, createChannelDataSource } from '../utils/channel-data-source.js';
import { extractGaplessMetadata, type GaplessMetadata } from '../utils/metadata-parser.js';

export interface CachedAudio {
  source: ChannelDataSource;
  metadata: GaplessMetadata;
}

export interface AudioCacheStats {
  totalCached: number;
  totalMemoryBytes: number;
  urls: string[];
}

export class AudioCacheManager {
  private cache = new Map<string, CachedAudio>();
  private audioContext: AudioContext;

  constructor(audioContext: AudioContext) {
    this.audioContext = audioContext;
  }

  /**
   * Get or load audio from cache
   * @param url URL of the audio file
   * @returns Promise resolving to cached audio
   */
  async getOrLoad(url: string): Promise<CachedAudio> {
    // Check if already cached
    const cached = this.cache.get(url);
    if (cached) {
      console.log('✅ Audio file already cached:', url);
      return cached;
    }

    // Load new audio
    return this.load(url);
  }

  /**
   * Load audio and add to cache
   * @param url URL of the audio file
   * @returns Promise resolving to cached audio
   */
  async load(url: string): Promise<CachedAudio> {
    // Extract metadata
    const metadata = await extractGaplessMetadata(url);
    console.log('🎵 Extracted gapless metadata:', metadata);

    // Create channel data source
    const source = createChannelDataSource(this.audioContext, url, metadata);

    // Cache it
    const cached: CachedAudio = { source, metadata };
    this.cache.set(url, cached);

    return cached;
  }

  /**
   * Preload audio without returning it
   * @param url URL of the audio file
   */
  async preload(url: string): Promise<void> {
    const cached = await this.getOrLoad(url);
    // Ensure audio buffer is loaded
    await cached.source.loadAudioBuffer();
    console.log('✅ Audio file queued and ready:', url);
  }

  /**
   * Check if URL is cached
   * @param url URL to check
   * @returns true if cached
   */
  has(url: string): boolean {
    return this.cache.has(url);
  }

  /**
   * Get cached audio without loading
   * @param url URL of the audio file
   * @returns Cached audio or undefined
   */
  get(url: string): CachedAudio | undefined {
    return this.cache.get(url);
  }

  /**
   * Remove audio from cache
   * @param url URL to remove
   * @returns true if removed, false if not found
   */
  remove(url: string): boolean {
    return this.cache.delete(url);
  }

  /**
   * Clear entire cache
   */
  clear(): void {
    this.cache.clear();
  }

  /**
   * Get cache statistics
   * @returns Cache statistics
   */
  getStats(): AudioCacheStats {
    const urls = Array.from(this.cache.keys());
    let totalMemoryBytes = 0;

    // Calculate approximate memory usage
    for (const cached of this.cache.values()) {
      const buffer = cached.source.getAudioBuffer();
      if (buffer) {
        // Each sample is 4 bytes (Float32)
        totalMemoryBytes += buffer.length * buffer.numberOfChannels * 4;
      }
    }

    return {
      totalCached: this.cache.size,
      totalMemoryBytes,
      urls
    };
  }

  /**
   * Get all cached URLs
   * @returns Array of cached URLs
   */
  getCachedUrls(): string[] {
    return Array.from(this.cache.keys());
  }

  /**
   * Preload multiple URLs concurrently
   * @param urls Array of URLs to preload
   * @returns Promise that resolves when all are loaded
   */
  async preloadMultiple(urls: string[]): Promise<void> {
    await Promise.all(urls.map(url => this.preload(url)));
  }

  /**
   * Remove least recently used items if cache is too large
   * @param maxItems Maximum number of items to keep
   */
  pruneCache(maxItems: number): void {
    if (this.cache.size <= maxItems) {
      return;
    }

    // Simple strategy: remove oldest entries
    // In a real implementation, you'd track access times
    const entries = Array.from(this.cache.entries());
    const toRemove = entries.slice(0, entries.length - maxItems);

    for (const [url] of toRemove) {
      this.cache.delete(url);
      console.log('🗑️ Pruned from cache:', url);
    }
  }
}
